"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * ScheduleCell - Composant pour afficher les événements programmés de la journée
 * 
 * @param {Object} props
 * @param {Array} props.scheduled_events - Liste des événements
 * @param {Object} [props.styles_properties] - Propriétés de style
 * @param {string} [props.template_name] - Nom du template (default, corporate, neon, nature)
 * @param {number} [props.scaleFactor] - Facteur d'échelle
 */const ScheduleCell = ({
  scheduled_events = [],
  styles_properties = {},
  template_name = 'default',
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  switch (template_name) {
    case 'corporate':
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ScheduleCellCorporate, {
        scheduled_events: scheduled_events,
        styles_properties: styles_properties,
        scaleFactor: scaleFactor,
        uri_transformer: uri_transformer,
        ...rest
      });
    case 'neon':
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ScheduleCellNeon, {
        scheduled_events: scheduled_events,
        styles_properties: styles_properties,
        scaleFactor: scaleFactor,
        uri_transformer: uri_transformer,
        ...rest
      });
    case 'nature':
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ScheduleCellNature, {
        scheduled_events: scheduled_events,
        styles_properties: styles_properties,
        scaleFactor: scaleFactor,
        uri_transformer: uri_transformer,
        ...rest
      });
    case 'retro':
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ScheduleCellRetro, {
        scheduled_events: scheduled_events,
        styles_properties: styles_properties,
        scaleFactor: scaleFactor,
        uri_transformer: uri_transformer,
        ...rest
      });
    case 'minimal':
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ScheduleCellMinimal, {
        scheduled_events: scheduled_events,
        styles_properties: styles_properties,
        scaleFactor: scaleFactor,
        uri_transformer: uri_transformer,
        ...rest
      });
    case 'default':
    default:
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ScheduleCellDefault, {
        scheduled_events: scheduled_events,
        styles_properties: styles_properties,
        scaleFactor: scaleFactor,
        uri_transformer: uri_transformer,
        ...rest
      });
  }
};

// ============================================
// HOOKS COMMUNS
// ============================================

const useScheduleEvents = scheduled_events => {
  const [currentTime, setCurrentTime] = (0, _react.useState)(new Date());
  (0, _react.useEffect)(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);
  const todayEvents = (0, _react.useMemo)(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return scheduled_events.filter(event => {
      const eventStart = new Date(event.start_time);
      return eventStart >= today && eventStart < tomorrow;
    }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  }, [scheduled_events]);
  const formatTime = date => {
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatDate = () => {
    return currentTime.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const isEventNow = event => {
    const now = currentTime;
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    return now >= start && now <= end;
  };
  const isEventPast = event => {
    return new Date(event.end_time) < currentTime;
  };
  return {
    currentTime,
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  };
};

// ============================================
// TEMPLATE DEFAULT
// ============================================

const ScheduleCellDefault = ({
  scheduled_events = [],
  styles_properties = {},
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  const [visibleEvents, setVisibleEvents] = (0, _react.useState)(new Set());
  const {
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  } = useScheduleEvents(scheduled_events);
  (0, _react.useEffect)(() => {
    todayEvents.forEach((event, index) => {
      setTimeout(() => {
        setVisibleEvents(prev => new Set([...prev, event.id]));
      }, index * 150);
    });
  }, [todayEvents]);
  const primaryColor = styles_properties.primary_color || '#6366f1';
  const secondaryColor = styles_properties.secondary_color || '#a855f7';
  const backgroundColor = styles_properties.background_color || '#0f0f1a';
  const textColor = styles_properties.text_color || '#f1f5f9';
  const textMuted = styles_properties.text_muted || '#94a3b8';
  const fontFamily = styles_properties.font_family || "'Outfit', 'Inter', system-ui, sans-serif";
  const keyframesStyle = `
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-5px); } }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); } 50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6); } }
    `;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      fontFamily,
      background: `linear-gradient(165deg, ${backgroundColor} 0%, #1a1a2e 50%, ${backgroundColor} 100%)`,
      color: textColor,
      height: '100%',
      width: '100%',
      padding: `${24 * scaleFactor}px`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    },
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframesStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: `${300 * scaleFactor}px`,
        height: `${300 * scaleFactor}px`,
        background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none'
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        bottom: '-30%',
        left: '-10%',
        width: `${200 * scaleFactor}px`,
        height: `${200 * scaleFactor}px`,
        background: `radial-gradient(circle, ${secondaryColor}10 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none'
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("header", {
      style: {
        marginBottom: `${24 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          fontSize: `${14 * scaleFactor}px`,
          color: primaryColor,
          textTransform: 'uppercase',
          letterSpacing: `${2 * scaleFactor}px`,
          fontWeight: 600,
          marginBottom: `${8 * scaleFactor}px`
        },
        children: ["\uD83D\uDCC5 ", formatDate()]
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        style: {
          fontSize: `${28 * scaleFactor}px`,
          fontWeight: 700,
          margin: 0,
          background: `linear-gradient(135deg, ${textColor} 0%, ${primaryColor} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        },
        children: "Programme du jour"
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${8 * scaleFactor}px`,
          marginTop: `${12 * scaleFactor}px`,
          fontSize: `${13 * scaleFactor}px`,
          color: textMuted
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            display: 'inline-block',
            width: `${8 * scaleFactor}px`,
            height: `${8 * scaleFactor}px`,
            backgroundColor: '#10b981',
            borderRadius: '50%',
            animation: 'pulse 2s ease-in-out infinite'
          }
        }), todayEvents.length, " \xE9v\xE9nement", todayEvents.length > 1 ? 's' : '', " pr\xE9vu", todayEvents.length > 1 ? 's' : '']
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: `${16 * scaleFactor}px`,
        paddingRight: `${8 * scaleFactor}px`
      },
      children: todayEvents.length === 0 ? /*#__PURE__*/(0, _jsxRuntime.jsx)(EmptyState, {
        scaleFactor: scaleFactor,
        textMuted: textMuted
      }) : todayEvents.map(event => /*#__PURE__*/(0, _jsxRuntime.jsx)(DefaultEventCard, {
        event: event,
        isNow: isEventNow(event),
        isPast: isEventPast(event),
        isVisible: visibleEvents.has(event.id),
        scaleFactor: scaleFactor,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        textColor: textColor,
        textMuted: textMuted,
        formatTime: formatTime,
        uri_transformer: uri_transformer
      }, event.id))
    })]
  });
};
const DefaultEventCard = ({
  event,
  isNow,
  isPast,
  isVisible,
  scaleFactor,
  primaryColor,
  secondaryColor,
  textColor,
  textMuted,
  formatTime,
  uri_transformer
}) => {
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      gap: `${16 * scaleFactor}px`,
      padding: `${20 * scaleFactor}px`,
      background: isNow ? `linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.1) 100%)` : isPast ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.05)',
      border: `1px solid ${isNow ? primaryColor + '50' : 'rgba(255, 255, 255, 0.08)'}`,
      borderRadius: `${16 * scaleFactor}px`,
      position: 'relative',
      overflow: 'hidden',
      opacity: isVisible ? isPast ? 0.5 : 1 : 0,
      transform: isVisible ? `translateX(0) scale(${isHovered ? 1.02 : 1})` : 'translateX(-30px)',
      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      cursor: 'pointer',
      animation: isNow ? 'glow 3s ease-in-out infinite' : 'none'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [isNow && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        position: 'absolute',
        top: `${12 * scaleFactor}px`,
        right: `${12 * scaleFactor}px`,
        display: 'flex',
        alignItems: 'center',
        gap: `${6 * scaleFactor}px`,
        padding: `${4 * scaleFactor}px ${10 * scaleFactor}px`,
        background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
        borderRadius: `${20 * scaleFactor}px`,
        fontSize: `${11 * scaleFactor}px`,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: `${1 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          width: `${6 * scaleFactor}px`,
          height: `${6 * scaleFactor}px`,
          backgroundColor: 'white',
          borderRadius: '50%',
          animation: 'pulse 1s ease-in-out infinite'
        }
      }), "En cours"]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: `${70 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${15 * scaleFactor}px`,
          fontWeight: 700,
          color: isNow ? primaryColor : textColor
        },
        children: formatTime(event.start_time)
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          width: `${2 * scaleFactor}px`,
          flex: 1,
          background: `linear-gradient(180deg, ${primaryColor}50 0%, ${secondaryColor}30 100%)`,
          margin: `${8 * scaleFactor}px 0`,
          borderRadius: `${2 * scaleFactor}px`
        }
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${13 * scaleFactor}px`,
          fontWeight: 700,
          color: isNow ? primaryColor : textColor,
          opacity: 0.7
        },
        children: formatTime(event.end_time)
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: `${8 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h3", {
        style: {
          margin: 0,
          fontSize: `${18 * scaleFactor}px`,
          fontWeight: 600,
          color: textColor,
          lineHeight: 1.3
        },
        children: event.title
      }), event.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        style: {
          margin: 0,
          fontSize: `${14 * scaleFactor}px`,
          color: textMuted,
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        },
        children: event.description
      }), event.location && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${6 * scaleFactor}px`,
          fontSize: `${13 * scaleFactor}px`,
          color: primaryColor,
          marginTop: `${4 * scaleFactor}px`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          children: "\uD83D\uDCCD"
        }), event.location]
      })]
    }), event.logo_uri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
      src: uri_transformer(event.logo_uri),
      alt: event.title,
      style: {
        width: `${60 * scaleFactor}px`,
        height: `${60 * scaleFactor}px`,
        borderRadius: `${12 * scaleFactor}px`,
        objectFit: 'cover',
        border: `2px solid ${primaryColor}30`,
        animation: isHovered ? 'float 2s ease-in-out infinite' : 'none'
      },
      onError: e => {
        e.target.style.display = 'none';
      }
    })]
  });
};

// ============================================
// TEMPLATE CORPORATE
// ============================================

const ScheduleCellCorporate = ({
  scheduled_events = [],
  styles_properties = {},
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  const [visibleEvents, setVisibleEvents] = (0, _react.useState)(new Set());
  const {
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  } = useScheduleEvents(scheduled_events);
  (0, _react.useEffect)(() => {
    todayEvents.forEach((event, index) => {
      setTimeout(() => {
        setVisibleEvents(prev => new Set([...prev, event.id]));
      }, index * 100);
    });
  }, [todayEvents]);
  const primaryColor = styles_properties.primary_color || '#0ea5e9';
  const accentColor = styles_properties.secondary_color || '#0284c7';
  const backgroundColor = styles_properties.background_color || '#ffffff';
  const textColor = styles_properties.text_color || '#0f172a';
  const textMuted = styles_properties.text_muted || '#64748b';
  const fontFamily = styles_properties.font_family || "'Inter', 'Segoe UI', system-ui, sans-serif";
  const keyframesStyle = `
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
    `;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      fontFamily,
      background: backgroundColor,
      color: textColor,
      height: '100%',
      width: '100%',
      padding: `${32 * scaleFactor}px`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframesStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("header", {
      style: {
        marginBottom: `${28 * scaleFactor}px`,
        borderBottom: `2px solid ${primaryColor}`,
        paddingBottom: `${20 * scaleFactor}px`
      },
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              fontSize: `${12 * scaleFactor}px`,
              color: textMuted,
              textTransform: 'uppercase',
              letterSpacing: `${1.5 * scaleFactor}px`,
              fontWeight: 500,
              marginBottom: `${4 * scaleFactor}px`
            },
            children: "Planning journalier"
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
            style: {
              fontSize: `${24 * scaleFactor}px`,
              fontWeight: 700,
              margin: 0,
              color: textColor
            },
            children: formatDate().charAt(0).toUpperCase() + formatDate().slice(1)
          })]
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            display: 'flex',
            alignItems: 'center',
            gap: `${12 * scaleFactor}px`,
            padding: `${12 * scaleFactor}px ${20 * scaleFactor}px`,
            background: `${primaryColor}10`,
            borderRadius: `${8 * scaleFactor}px`,
            border: `1px solid ${primaryColor}20`
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              width: `${10 * scaleFactor}px`,
              height: `${10 * scaleFactor}px`,
              backgroundColor: primaryColor,
              borderRadius: '50%'
            }
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
            style: {
              fontSize: `${14 * scaleFactor}px`,
              fontWeight: 600,
              color: primaryColor
            },
            children: [todayEvents.length, " \xE9v\xE9nement", todayEvents.length > 1 ? 's' : '']
          })]
        })]
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: `${12 * scaleFactor}px`
      },
      children: todayEvents.length === 0 ? /*#__PURE__*/(0, _jsxRuntime.jsx)(CorporateEmptyState, {
        scaleFactor: scaleFactor,
        textMuted: textMuted,
        primaryColor: primaryColor
      }) : todayEvents.map((event, index) => /*#__PURE__*/(0, _jsxRuntime.jsx)(CorporateEventCard, {
        event: event,
        index: index,
        isNow: isEventNow(event),
        isPast: isEventPast(event),
        isVisible: visibleEvents.has(event.id),
        scaleFactor: scaleFactor,
        primaryColor: primaryColor,
        accentColor: accentColor,
        textColor: textColor,
        textMuted: textMuted,
        formatTime: formatTime,
        uri_transformer: uri_transformer
      }, event.id))
    })]
  });
};
const CorporateEventCard = ({
  event,
  index,
  isNow,
  isPast,
  isVisible,
  scaleFactor,
  primaryColor,
  accentColor,
  textColor,
  textMuted,
  formatTime,
  uri_transformer
}) => {
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      alignItems: 'stretch',
      gap: `${16 * scaleFactor}px`,
      padding: `${16 * scaleFactor}px ${20 * scaleFactor}px`,
      background: isNow ? `${primaryColor}08` : isHovered ? '#f8fafc' : '#ffffff',
      border: `1px solid ${isNow ? primaryColor : isHovered ? '#e2e8f0' : '#f1f5f9'}`,
      borderRadius: `${12 * scaleFactor}px`,
      opacity: isVisible ? isPast ? 0.5 : 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: `${80 * scaleFactor}px`,
        padding: `${12 * scaleFactor}px`,
        background: isNow ? primaryColor : '#f1f5f9',
        borderRadius: `${8 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${16 * scaleFactor}px`,
          fontWeight: 700,
          color: isNow ? '#ffffff' : textColor
        },
        children: formatTime(event.start_time)
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          width: `${20 * scaleFactor}px`,
          height: `${1 * scaleFactor}px`,
          background: isNow ? 'rgba(255,255,255,0.3)' : '#cbd5e1',
          margin: `${6 * scaleFactor}px 0`
        }
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${14 * scaleFactor}px`,
          fontWeight: 500,
          color: isNow ? 'rgba(255,255,255,0.8)' : textMuted
        },
        children: formatTime(event.end_time)
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: `${6 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${10 * scaleFactor}px`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h3", {
          style: {
            margin: 0,
            fontSize: `${16 * scaleFactor}px`,
            fontWeight: 600,
            color: textColor
          },
          children: event.title
        }), isNow && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            padding: `${2 * scaleFactor}px ${8 * scaleFactor}px`,
            background: '#22c55e',
            color: 'white',
            fontSize: `${10 * scaleFactor}px`,
            fontWeight: 700,
            borderRadius: `${4 * scaleFactor}px`,
            textTransform: 'uppercase'
          },
          children: "En cours"
        })]
      }), event.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        style: {
          margin: 0,
          fontSize: `${13 * scaleFactor}px`,
          color: textMuted,
          lineHeight: 1.4
        },
        children: event.description
      }), event.location && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${4 * scaleFactor}px`,
          fontSize: `${12 * scaleFactor}px`,
          color: accentColor,
          fontWeight: 500
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          children: "\uD83D\uDCCD"
        }), event.location]
      })]
    }), event.logo_uri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
      src: uri_transformer(event.logo_uri),
      alt: event.title,
      style: {
        width: `${50 * scaleFactor}px`,
        height: `${50 * scaleFactor}px`,
        borderRadius: `${8 * scaleFactor}px`,
        objectFit: 'cover',
        alignSelf: 'center'
      },
      onError: e => {
        e.target.style.display = 'none';
      }
    })]
  });
};
const CorporateEmptyState = ({
  scaleFactor,
  textMuted,
  primaryColor
}) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
  style: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: `${20 * scaleFactor}px`,
    padding: `${40 * scaleFactor}px`
  },
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      width: `${80 * scaleFactor}px`,
      height: `${80 * scaleFactor}px`,
      background: `${primaryColor}10`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${36 * scaleFactor}px`
    },
    children: "\uD83D\uDCCB"
  }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${18 * scaleFactor}px`,
        fontWeight: 600,
        marginBottom: `${8 * scaleFactor}px`,
        color: '#0f172a'
      },
      children: "Aucun \xE9v\xE9nement programm\xE9"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${14 * scaleFactor}px`,
        color: textMuted
      },
      children: "Votre journ\xE9e est libre"
    })]
  })]
});

// ============================================
// TEMPLATE NEON
// ============================================

const ScheduleCellNeon = ({
  scheduled_events = [],
  styles_properties = {},
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  const [visibleEvents, setVisibleEvents] = (0, _react.useState)(new Set());
  const {
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  } = useScheduleEvents(scheduled_events);
  (0, _react.useEffect)(() => {
    todayEvents.forEach((event, index) => {
      setTimeout(() => {
        setVisibleEvents(prev => new Set([...prev, event.id]));
      }, index * 200);
    });
  }, [todayEvents]);
  const neonPink = styles_properties.primary_color || '#ff00ff';
  const neonCyan = styles_properties.secondary_color || '#00ffff';
  const neonYellow = '#ffff00';
  const backgroundColor = styles_properties.background_color || '#0a0a0f';
  const textColor = styles_properties.text_color || '#ffffff';
  const textMuted = styles_properties.text_muted || '#888899';
  const fontFamily = styles_properties.font_family || "'Orbitron', 'Rajdhani', monospace, sans-serif";
  const keyframesStyle = `
        @keyframes neonFlicker { 0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% { opacity: 1; } 20%, 24%, 55% { opacity: 0.6; } }
        @keyframes neonPulse { 0%, 100% { filter: drop-shadow(0 0 5px ${neonPink}) drop-shadow(0 0 10px ${neonPink}); } 50% { filter: drop-shadow(0 0 15px ${neonPink}) drop-shadow(0 0 30px ${neonPink}); } }
        @keyframes scanline { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } }
        @keyframes glitch { 0%, 100% { transform: translate(0); } 20% { transform: translate(-2px, 2px); } 40% { transform: translate(-2px, -2px); } 60% { transform: translate(2px, 2px); } 80% { transform: translate(2px, -2px); } }
        @keyframes borderGlow { 0%, 100% { border-color: ${neonCyan}; box-shadow: 0 0 10px ${neonCyan}, inset 0 0 10px ${neonCyan}20; } 50% { border-color: ${neonPink}; box-shadow: 0 0 20px ${neonPink}, inset 0 0 20px ${neonPink}20; } }
    `;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      fontFamily,
      background: `linear-gradient(180deg, ${backgroundColor} 0%, #0f0f1a 50%, ${backgroundColor} 100%)`,
      color: textColor,
      height: '100%',
      width: '100%',
      padding: `${24 * scaleFactor}px`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    },
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframesStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        inset: 0,
        backgroundImage: `linear-gradient(${neonCyan}10 1px, transparent 1px), linear-gradient(90deg, ${neonCyan}10 1px, transparent 1px)`,
        backgroundSize: `${40 * scaleFactor}px ${40 * scaleFactor}px`,
        pointerEvents: 'none',
        opacity: 0.5
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(transparent 50%, ${backgroundColor}80 50%)`,
        backgroundSize: `100% ${4 * scaleFactor}px`,
        pointerEvents: 'none',
        opacity: 0.1
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("header", {
      style: {
        marginBottom: `${28 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          fontSize: `${11 * scaleFactor}px`,
          color: neonCyan,
          textTransform: 'uppercase',
          letterSpacing: `${4 * scaleFactor}px`,
          fontWeight: 400,
          marginBottom: `${8 * scaleFactor}px`,
          textShadow: `0 0 10px ${neonCyan}`
        },
        children: "\u25B8 SYSTEM.SCHEDULE"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        style: {
          fontSize: `${32 * scaleFactor}px`,
          fontWeight: 700,
          margin: 0,
          color: neonPink,
          textShadow: `0 0 20px ${neonPink}, 0 0 40px ${neonPink}50`,
          animation: 'neonFlicker 3s infinite'
        },
        children: formatDate().toUpperCase()
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${12 * scaleFactor}px`,
          marginTop: `${16 * scaleFactor}px`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            padding: `${6 * scaleFactor}px ${14 * scaleFactor}px`,
            border: `1px solid ${neonCyan}`,
            background: `${neonCyan}10`,
            fontSize: `${12 * scaleFactor}px`,
            color: neonCyan,
            textShadow: `0 0 5px ${neonCyan}`
          },
          children: ["EVENTS: ", todayEvents.length]
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            padding: `${6 * scaleFactor}px ${14 * scaleFactor}px`,
            border: `1px solid ${neonPink}`,
            background: `${neonPink}10`,
            fontSize: `${12 * scaleFactor}px`,
            color: neonPink,
            textShadow: `0 0 5px ${neonPink}`
          },
          children: "STATUS: ONLINE"
        })]
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: `${16 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: todayEvents.length === 0 ? /*#__PURE__*/(0, _jsxRuntime.jsx)(NeonEmptyState, {
        scaleFactor: scaleFactor,
        neonCyan: neonCyan,
        neonPink: neonPink
      }) : todayEvents.map(event => /*#__PURE__*/(0, _jsxRuntime.jsx)(NeonEventCard, {
        event: event,
        isNow: isEventNow(event),
        isPast: isEventPast(event),
        isVisible: visibleEvents.has(event.id),
        scaleFactor: scaleFactor,
        neonPink: neonPink,
        neonCyan: neonCyan,
        neonYellow: neonYellow,
        textColor: textColor,
        textMuted: textMuted,
        formatTime: formatTime,
        uri_transformer: uri_transformer
      }, event.id))
    })]
  });
};
const NeonEventCard = ({
  event,
  isNow,
  isPast,
  isVisible,
  scaleFactor,
  neonPink,
  neonCyan,
  neonYellow,
  textColor,
  textMuted,
  formatTime,
  uri_transformer
}) => {
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  const activeColor = isNow ? neonYellow : neonCyan;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      gap: `${16 * scaleFactor}px`,
      padding: `${20 * scaleFactor}px`,
      background: `linear-gradient(135deg, ${activeColor}08 0%, transparent 100%)`,
      border: `1px solid ${isNow ? neonYellow : isHovered ? neonPink : neonCyan}50`,
      borderRadius: `${4 * scaleFactor}px`,
      position: 'relative',
      overflow: 'hidden',
      opacity: isVisible ? isPast ? 0.4 : 1 : 0,
      transform: isVisible ? `translateX(0) ${isHovered ? 'scale(1.01)' : ''}` : 'translateX(-50px)',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      boxShadow: isHovered ? `0 0 30px ${neonPink}30, inset 0 0 30px ${neonPink}10` : `0 0 15px ${activeColor}20`,
      animation: isNow ? 'borderGlow 2s infinite' : 'none'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [isNow && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: `${8 * scaleFactor}px`,
        right: `${8 * scaleFactor}px`,
        padding: `${4 * scaleFactor}px ${12 * scaleFactor}px`,
        background: neonYellow,
        color: '#000',
        fontSize: `${10 * scaleFactor}px`,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: `${2 * scaleFactor}px`,
        animation: 'glitch 0.5s infinite'
      },
      children: "\u25B8 LIVE"
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: `${80 * scaleFactor}px`,
        borderRight: `1px solid ${activeColor}30`,
        paddingRight: `${16 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${18 * scaleFactor}px`,
          fontWeight: 700,
          color: activeColor,
          textShadow: `0 0 10px ${activeColor}`,
          fontFamily: 'monospace'
        },
        children: formatTime(event.start_time)
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          width: `${1 * scaleFactor}px`,
          height: `${20 * scaleFactor}px`,
          background: `linear-gradient(180deg, ${activeColor} 0%, transparent 100%)`,
          margin: `${8 * scaleFactor}px 0`
        }
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${14 * scaleFactor}px`,
          color: textMuted,
          fontFamily: 'monospace'
        },
        children: formatTime(event.end_time)
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: `${8 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h3", {
        style: {
          margin: 0,
          fontSize: `${17 * scaleFactor}px`,
          fontWeight: 600,
          color: textColor,
          textTransform: 'uppercase',
          letterSpacing: `${1 * scaleFactor}px`
        },
        children: event.title
      }), event.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        style: {
          margin: 0,
          fontSize: `${13 * scaleFactor}px`,
          color: textMuted,
          lineHeight: 1.5
        },
        children: event.description
      }), event.location && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${6 * scaleFactor}px`,
          fontSize: `${12 * scaleFactor}px`,
          color: neonPink,
          textShadow: `0 0 5px ${neonPink}`
        },
        children: ["\u25C8 ", event.location]
      })]
    }), event.logo_uri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
      src: uri_transformer(event.logo_uri),
      alt: event.title,
      style: {
        width: `${55 * scaleFactor}px`,
        height: `${55 * scaleFactor}px`,
        borderRadius: `${4 * scaleFactor}px`,
        objectFit: 'cover',
        border: `1px solid ${neonCyan}50`,
        filter: `drop-shadow(0 0 5px ${neonCyan})`
      },
      onError: e => {
        e.target.style.display = 'none';
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${15 * scaleFactor}px`,
        height: `${1 * scaleFactor}px`,
        background: activeColor
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${1 * scaleFactor}px`,
        height: `${15 * scaleFactor}px`,
        background: activeColor
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: `${15 * scaleFactor}px`,
        height: `${1 * scaleFactor}px`,
        background: activeColor
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: `${1 * scaleFactor}px`,
        height: `${15 * scaleFactor}px`,
        background: activeColor
      }
    })]
  });
};
const NeonEmptyState = ({
  scaleFactor,
  neonCyan,
  neonPink
}) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
  style: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: `${20 * scaleFactor}px`
  },
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      fontSize: `${60 * scaleFactor}px`,
      filter: `drop-shadow(0 0 20px ${neonCyan})`,
      animation: 'neonFlicker 2s infinite'
    },
    children: "\u26A1"
  }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${16 * scaleFactor}px`,
        fontWeight: 600,
        color: neonPink,
        textShadow: `0 0 10px ${neonPink}`,
        textTransform: 'uppercase',
        letterSpacing: `${3 * scaleFactor}px`
      },
      children: "NO DATA FOUND"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: `${8 * scaleFactor}px 0 0`,
        fontSize: `${12 * scaleFactor}px`,
        color: neonCyan,
        fontFamily: 'monospace'
      },
      children: '>>> SCHEDULE_EMPTY'
    })]
  })]
});

// ============================================
// TEMPLATE NATURE
// ============================================

const ScheduleCellNature = ({
  scheduled_events = [],
  styles_properties = {},
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  const [visibleEvents, setVisibleEvents] = (0, _react.useState)(new Set());
  const {
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  } = useScheduleEvents(scheduled_events);
  (0, _react.useEffect)(() => {
    todayEvents.forEach((event, index) => {
      setTimeout(() => {
        setVisibleEvents(prev => new Set([...prev, event.id]));
      }, index * 180);
    });
  }, [todayEvents]);
  const leafGreen = styles_properties.primary_color || '#22c55e';
  const earthBrown = styles_properties.secondary_color || '#a16207';
  const skyBlue = '#38bdf8';
  const backgroundColor = styles_properties.background_color || '#fefce8';
  const textColor = styles_properties.text_color || '#365314';
  const textMuted = styles_properties.text_muted || '#65a30d';
  const fontFamily = styles_properties.font_family || "'Quicksand', 'Nunito', 'Poppins', sans-serif";
  const keyframesStyle = `
        @keyframes sway { 0%, 100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes leafFall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 0; } 50% { opacity: 1; } 100% { transform: translateY(20px) rotate(360deg); opacity: 0; } }
        @keyframes sunRays { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.6; } }
    `;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      fontFamily,
      background: `linear-gradient(180deg, ${backgroundColor} 0%, #ecfccb 50%, #d9f99d 100%)`,
      color: textColor,
      height: '100%',
      width: '100%',
      padding: `${24 * scaleFactor}px`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    },
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframesStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: `${-50 * scaleFactor}px`,
        right: `${-50 * scaleFactor}px`,
        width: `${150 * scaleFactor}px`,
        height: `${150 * scaleFactor}px`,
        background: `radial-gradient(circle, #fbbf24 0%, #f59e0b40 40%, transparent 70%)`,
        borderRadius: '50%',
        animation: 'sunRays 4s ease-in-out infinite',
        pointerEvents: 'none'
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: `${20 * scaleFactor}px`,
        left: `${10 * scaleFactor}px`,
        fontSize: `${24 * scaleFactor}px`,
        animation: 'sway 3s ease-in-out infinite',
        opacity: 0.6
      },
      children: "\uD83C\uDF43"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        bottom: `${30 * scaleFactor}px`,
        right: `${20 * scaleFactor}px`,
        fontSize: `${20 * scaleFactor}px`,
        animation: 'sway 4s ease-in-out infinite',
        animationDelay: '1s',
        opacity: 0.5
      },
      children: "\uD83C\uDF3F"
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("header", {
      style: {
        marginBottom: `${24 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${8 * scaleFactor}px`,
          fontSize: `${13 * scaleFactor}px`,
          color: earthBrown,
          marginBottom: `${8 * scaleFactor}px`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            animation: 'float 3s ease-in-out infinite'
          },
          children: "\uD83C\uDF3B"
        }), formatDate()]
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        style: {
          fontSize: `${26 * scaleFactor}px`,
          fontWeight: 700,
          margin: 0,
          color: textColor
        },
        children: "Votre Journ\xE9e"
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: `${8 * scaleFactor}px`,
          marginTop: `${12 * scaleFactor}px`,
          padding: `${8 * scaleFactor}px ${16 * scaleFactor}px`,
          background: 'rgba(255,255,255,0.7)',
          borderRadius: `${20 * scaleFactor}px`,
          border: `2px solid ${leafGreen}30`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          children: "\uD83C\uDF31"
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
          style: {
            fontSize: `${13 * scaleFactor}px`,
            fontWeight: 600,
            color: leafGreen
          },
          children: [todayEvents.length, " moment", todayEvents.length > 1 ? 's' : '', " de bonheur"]
        })]
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: `${14 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: todayEvents.length === 0 ? /*#__PURE__*/(0, _jsxRuntime.jsx)(NatureEmptyState, {
        scaleFactor: scaleFactor,
        textMuted: textMuted,
        leafGreen: leafGreen
      }) : todayEvents.map(event => /*#__PURE__*/(0, _jsxRuntime.jsx)(NatureEventCard, {
        event: event,
        isNow: isEventNow(event),
        isPast: isEventPast(event),
        isVisible: visibleEvents.has(event.id),
        scaleFactor: scaleFactor,
        leafGreen: leafGreen,
        earthBrown: earthBrown,
        skyBlue: skyBlue,
        textColor: textColor,
        textMuted: textMuted,
        formatTime: formatTime,
        uri_transformer: uri_transformer
      }, event.id))
    })]
  });
};
const NatureEventCard = ({
  event,
  isNow,
  isPast,
  isVisible,
  scaleFactor,
  leafGreen,
  earthBrown,
  skyBlue,
  textColor,
  textMuted,
  formatTime,
  uri_transformer
}) => {
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      gap: `${16 * scaleFactor}px`,
      padding: `${18 * scaleFactor}px`,
      background: isNow ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.7)',
      border: `2px solid ${isNow ? leafGreen : isHovered ? earthBrown + '50' : 'transparent'}`,
      borderRadius: `${20 * scaleFactor}px`,
      position: 'relative',
      overflow: 'hidden',
      opacity: isVisible ? isPast ? 0.5 : 1 : 0,
      transform: isVisible ? `translateY(0) scale(${isHovered ? 1.02 : 1})` : 'translateY(30px)',
      transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      cursor: 'pointer',
      boxShadow: isNow ? `0 8px 30px ${leafGreen}30` : isHovered ? '0 8px 25px rgba(0,0,0,0.1)' : '0 4px 15px rgba(0,0,0,0.05)'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [isNow && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        position: 'absolute',
        top: `${10 * scaleFactor}px`,
        right: `${10 * scaleFactor}px`,
        display: 'flex',
        alignItems: 'center',
        gap: `${6 * scaleFactor}px`,
        padding: `${5 * scaleFactor}px ${12 * scaleFactor}px`,
        background: `linear-gradient(135deg, ${leafGreen} 0%, #16a34a 100%)`,
        borderRadius: `${15 * scaleFactor}px`,
        color: 'white',
        fontSize: `${11 * scaleFactor}px`,
        fontWeight: 600
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          animation: 'float 1s ease-in-out infinite'
        },
        children: "\uD83C\uDF38"
      }), "Maintenant"]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: `${70 * scaleFactor}px`,
        padding: `${10 * scaleFactor}px`,
        background: `linear-gradient(135deg, ${leafGreen}15 0%, ${skyBlue}10 100%)`,
        borderRadius: `${14 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${15 * scaleFactor}px`,
          fontWeight: 700,
          color: leafGreen
        },
        children: formatTime(event.start_time)
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${18 * scaleFactor}px`,
          margin: `${4 * scaleFactor}px 0`
        },
        children: "\u2193"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${13 * scaleFactor}px`,
          fontWeight: 500,
          color: textMuted
        },
        children: formatTime(event.end_time)
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: `${6 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h3", {
        style: {
          margin: 0,
          fontSize: `${17 * scaleFactor}px`,
          fontWeight: 600,
          color: textColor
        },
        children: event.title
      }), event.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        style: {
          margin: 0,
          fontSize: `${13 * scaleFactor}px`,
          color: textMuted,
          lineHeight: 1.5
        },
        children: event.description
      }), event.location && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${6 * scaleFactor}px`,
          fontSize: `${12 * scaleFactor}px`,
          color: earthBrown,
          fontWeight: 500
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          children: "\uD83C\uDFE1"
        }), event.location]
      })]
    }), event.logo_uri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
      src: uri_transformer(event.logo_uri),
      alt: event.title,
      style: {
        width: `${55 * scaleFactor}px`,
        height: `${55 * scaleFactor}px`,
        borderRadius: `${14 * scaleFactor}px`,
        objectFit: 'cover',
        border: `3px solid ${leafGreen}20`,
        alignSelf: 'center'
      },
      onError: e => {
        e.target.style.display = 'none';
      }
    })]
  });
};
const NatureEmptyState = ({
  scaleFactor,
  textMuted,
  leafGreen
}) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
  style: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: `${20 * scaleFactor}px`
  },
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      fontSize: `${70 * scaleFactor}px`,
      animation: 'sway 4s ease-in-out infinite'
    },
    children: "\uD83C\uDF33"
  }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${18 * scaleFactor}px`,
        fontWeight: 600,
        color: leafGreen,
        marginBottom: `${8 * scaleFactor}px`
      },
      children: "Un moment de calme"
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("p", {
      style: {
        margin: 0,
        fontSize: `${14 * scaleFactor}px`,
        color: textMuted
      },
      children: ["Pas d'\xE9v\xE9nement pr\xE9vu aujourd'hui.", /*#__PURE__*/(0, _jsxRuntime.jsx)("br", {}), "Profitez de la nature ! \uD83C\uDF40"]
    })]
  })]
});

// ============================================
// TEMPLATE RETRO
// ============================================

const ScheduleCellRetro = ({
  scheduled_events = [],
  styles_properties = {},
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  const [visibleEvents, setVisibleEvents] = (0, _react.useState)(new Set());
  const {
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  } = useScheduleEvents(scheduled_events);
  (0, _react.useEffect)(() => {
    todayEvents.forEach((event, index) => {
      setTimeout(() => {
        setVisibleEvents(prev => new Set([...prev, event.id]));
      }, index * 120);
    });
  }, [todayEvents]);
  const primaryColor = styles_properties.primary_color || '#f97316';
  const secondaryColor = styles_properties.secondary_color || '#eab308';
  const tertiaryColor = '#ec4899';
  const backgroundColor = styles_properties.background_color || '#1e1b4b';
  const textColor = styles_properties.text_color || '#fef3c7';
  const textMuted = styles_properties.text_muted || '#c4b5fd';
  const fontFamily = styles_properties.font_family || "'VT323', 'Press Start 2P', 'Courier New', monospace";
  const keyframesStyle = `
        @keyframes retroBlink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        @keyframes retroSlide { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes retroPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes vhsNoise { 0%, 100% { opacity: 0.03; } 50% { opacity: 0.06; } }
        @keyframes rainbowBorder { 0% { border-color: ${primaryColor}; } 33% { border-color: ${secondaryColor}; } 66% { border-color: ${tertiaryColor}; } 100% { border-color: ${primaryColor}; } }
    `;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      fontFamily,
      background: `linear-gradient(135deg, ${backgroundColor} 0%, #312e81 50%, #1e1b4b 100%)`,
      color: textColor,
      height: '100%',
      width: '100%',
      padding: `${20 * scaleFactor}px`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    },
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframesStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        inset: 0,
        background: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)`,
        pointerEvents: 'none',
        animation: 'vhsNoise 0.5s infinite'
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        bottom: `${-80 * scaleFactor}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: `${250 * scaleFactor}px`,
        height: `${125 * scaleFactor}px`,
        background: `linear-gradient(180deg, ${primaryColor} 0%, ${secondaryColor} 50%, ${tertiaryColor} 100%)`,
        borderRadius: `${250 * scaleFactor}px ${250 * scaleFactor}px 0 0`,
        opacity: 0.3,
        pointerEvents: 'none'
      }
    }), [...Array(5)].map((_, i) => /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        bottom: `${(20 + i * 15) * scaleFactor}px`,
        left: 0,
        right: 0,
        height: `${2 * scaleFactor}px`,
        background: `linear-gradient(90deg, transparent 0%, ${tertiaryColor}40 50%, transparent 100%)`,
        pointerEvents: 'none'
      }
    }, i)), /*#__PURE__*/(0, _jsxRuntime.jsxs)("header", {
      style: {
        marginBottom: `${20 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${10 * scaleFactor}px`,
          marginBottom: `${8 * scaleFactor}px`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            fontSize: `${20 * scaleFactor}px`
          },
          children: "\uD83D\uDCFC"
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            fontSize: `${12 * scaleFactor}px`,
            color: primaryColor,
            letterSpacing: `${2 * scaleFactor}px`
          },
          children: "REC"
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            width: `${8 * scaleFactor}px`,
            height: `${8 * scaleFactor}px`,
            backgroundColor: '#ef4444',
            borderRadius: '50%',
            animation: 'retroBlink 1s infinite'
          }
        })]
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        style: {
          fontSize: `${28 * scaleFactor}px`,
          fontWeight: 400,
          margin: 0,
          color: secondaryColor,
          textShadow: `2px 2px 0 ${primaryColor}, 4px 4px 0 ${tertiaryColor}`
        },
        children: formatDate().toUpperCase()
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          marginTop: `${12 * scaleFactor}px`,
          padding: `${8 * scaleFactor}px ${16 * scaleFactor}px`,
          background: 'rgba(0,0,0,0.4)',
          border: `2px solid ${primaryColor}`,
          display: 'inline-block'
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
          style: {
            fontSize: `${14 * scaleFactor}px`,
            color: primaryColor
          },
          children: ["\u2605 ", todayEvents.length, " \xC9V\xC9NEMENT", todayEvents.length > 1 ? 'S' : '', " \u2605"]
        })
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: `${12 * scaleFactor}px`,
        position: 'relative',
        zIndex: 1
      },
      children: todayEvents.length === 0 ? /*#__PURE__*/(0, _jsxRuntime.jsx)(RetroEmptyState, {
        scaleFactor: scaleFactor,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor
      }) : todayEvents.map(event => /*#__PURE__*/(0, _jsxRuntime.jsx)(RetroEventCard, {
        event: event,
        isNow: isEventNow(event),
        isPast: isEventPast(event),
        isVisible: visibleEvents.has(event.id),
        scaleFactor: scaleFactor,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        tertiaryColor: tertiaryColor,
        textColor: textColor,
        textMuted: textMuted,
        formatTime: formatTime,
        uri_transformer: uri_transformer
      }, event.id))
    })]
  });
};
const RetroEventCard = ({
  event,
  isNow,
  isPast,
  isVisible,
  scaleFactor,
  primaryColor,
  secondaryColor,
  tertiaryColor,
  textColor,
  textMuted,
  formatTime,
  uri_transformer
}) => {
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      gap: `${14 * scaleFactor}px`,
      padding: `${16 * scaleFactor}px`,
      background: isNow ? `linear-gradient(90deg, ${primaryColor}20 0%, ${secondaryColor}20 100%)` : 'rgba(0,0,0,0.4)',
      border: `3px solid ${isNow ? secondaryColor : isHovered ? primaryColor : textMuted}50`,
      borderRadius: `${4 * scaleFactor}px`,
      position: 'relative',
      overflow: 'hidden',
      opacity: isVisible ? isPast ? 0.5 : 1 : 0,
      transform: isVisible ? `translateX(0) ${isHovered ? 'scale(1.02)' : ''}` : 'translateX(-100%)',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      animation: isNow ? 'rainbowBorder 2s infinite' : 'none'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [isNow && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        top: 0,
        right: 0,
        padding: `${4 * scaleFactor}px ${12 * scaleFactor}px`,
        background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
        fontSize: `${11 * scaleFactor}px`,
        fontWeight: 700,
        color: '#000',
        clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0 100%)'
      },
      children: "\u25B6 LIVE"
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: `${75 * scaleFactor}px`,
        padding: `${10 * scaleFactor}px`,
        background: `linear-gradient(180deg, ${primaryColor}30 0%, ${tertiaryColor}20 100%)`,
        border: `2px dashed ${secondaryColor}50`,
        borderRadius: `${4 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${18 * scaleFactor}px`,
          fontWeight: 400,
          color: secondaryColor
        },
        children: formatTime(event.start_time)
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${16 * scaleFactor}px`,
          color: tertiaryColor,
          margin: `${4 * scaleFactor}px 0`
        },
        children: "\u25BC"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${14 * scaleFactor}px`,
          color: textMuted
        },
        children: formatTime(event.end_time)
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: `${6 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h3", {
        style: {
          margin: 0,
          fontSize: `${18 * scaleFactor}px`,
          fontWeight: 400,
          color: textColor,
          textShadow: `1px 1px 0 ${primaryColor}`
        },
        children: event.title
      }), event.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        style: {
          margin: 0,
          fontSize: `${13 * scaleFactor}px`,
          color: textMuted,
          lineHeight: 1.4
        },
        children: event.description
      }), event.location && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${6 * scaleFactor}px`,
          fontSize: `${12 * scaleFactor}px`,
          color: secondaryColor
        },
        children: ["\u25C6 ", event.location]
      })]
    }), event.logo_uri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
      src: uri_transformer(event.logo_uri),
      alt: event.title,
      style: {
        width: `${50 * scaleFactor}px`,
        height: `${50 * scaleFactor}px`,
        borderRadius: `${4 * scaleFactor}px`,
        objectFit: 'cover',
        border: `3px solid ${primaryColor}`,
        alignSelf: 'center',
        filter: isHovered ? 'saturate(1.5)' : 'saturate(1)'
      },
      onError: e => {
        e.target.style.display = 'none';
      }
    })]
  });
};
const RetroEmptyState = ({
  scaleFactor,
  primaryColor,
  secondaryColor
}) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
  style: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: `${20 * scaleFactor}px`
  },
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      fontSize: `${60 * scaleFactor}px`,
      animation: 'retroPulse 2s infinite'
    },
    children: "\uD83D\uDCFA"
  }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${18 * scaleFactor}px`,
        color: secondaryColor,
        textShadow: `2px 2px 0 ${primaryColor}`
      },
      children: "NO SIGNAL"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: `${8 * scaleFactor}px 0 0`,
        fontSize: `${12 * scaleFactor}px`,
        color: primaryColor
      },
      children: "-- AUCUN \xC9V\xC9NEMENT --"
    })]
  })]
});

// ============================================
// TEMPLATE MINIMAL
// ============================================

const ScheduleCellMinimal = ({
  scheduled_events = [],
  styles_properties = {},
  scaleFactor = 1,
  uri_transformer = url => url,
  ...rest
}) => {
  const [visibleEvents, setVisibleEvents] = (0, _react.useState)(new Set());
  const {
    todayEvents,
    formatTime,
    formatDate,
    isEventNow,
    isEventPast
  } = useScheduleEvents(scheduled_events);
  (0, _react.useEffect)(() => {
    todayEvents.forEach((event, index) => {
      setTimeout(() => {
        setVisibleEvents(prev => new Set([...prev, event.id]));
      }, index * 80);
    });
  }, [todayEvents]);
  const accentColor = styles_properties.primary_color || '#000000';
  const backgroundColor = styles_properties.background_color || '#ffffff';
  const textColor = styles_properties.text_color || '#1a1a1a';
  const textMuted = styles_properties.text_muted || '#737373';
  const borderColor = styles_properties.secondary_color || '#e5e5e5';
  const fontFamily = styles_properties.font_family || "'Inter', 'Helvetica Neue', Arial, sans-serif";
  const keyframesStyle = `
        @keyframes minimalFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes minimalLine { from { width: 0; } to { width: 100%; } }
    `;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      fontFamily,
      background: backgroundColor,
      color: textColor,
      height: '100%',
      width: '100%',
      padding: `${32 * scaleFactor}px`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframesStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("header", {
      style: {
        marginBottom: `${32 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          fontSize: `${11 * scaleFactor}px`,
          color: textMuted,
          textTransform: 'uppercase',
          letterSpacing: `${3 * scaleFactor}px`,
          fontWeight: 500,
          marginBottom: `${12 * scaleFactor}px`
        },
        children: "Agenda"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        style: {
          fontSize: `${32 * scaleFactor}px`,
          fontWeight: 300,
          margin: 0,
          color: textColor,
          letterSpacing: `-${1 * scaleFactor}px`
        },
        children: formatDate()
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          marginTop: `${16 * scaleFactor}px`,
          height: `${1 * scaleFactor}px`,
          background: accentColor,
          animation: 'minimalLine 0.8s ease-out forwards'
        }
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      },
      children: todayEvents.length === 0 ? /*#__PURE__*/(0, _jsxRuntime.jsx)(MinimalEmptyState, {
        scaleFactor: scaleFactor,
        textMuted: textMuted,
        accentColor: accentColor
      }) : todayEvents.map((event, index) => /*#__PURE__*/(0, _jsxRuntime.jsx)(MinimalEventCard, {
        event: event,
        index: index,
        isNow: isEventNow(event),
        isPast: isEventPast(event),
        isVisible: visibleEvents.has(event.id),
        isLast: index === todayEvents.length - 1,
        scaleFactor: scaleFactor,
        accentColor: accentColor,
        textColor: textColor,
        textMuted: textMuted,
        borderColor: borderColor,
        formatTime: formatTime,
        uri_transformer: uri_transformer
      }, event.id))
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("footer", {
      style: {
        marginTop: `${24 * scaleFactor}px`,
        paddingTop: `${16 * scaleFactor}px`,
        borderTop: `1px solid ${borderColor}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
        style: {
          fontSize: `${12 * scaleFactor}px`,
          color: textMuted
        },
        children: [todayEvents.length, " \xE9v\xE9nement", todayEvents.length !== 1 ? 's' : '']
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${12 * scaleFactor}px`,
          color: textMuted
        },
        children: new Date().toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })
      })]
    })]
  });
};
const MinimalEventCard = ({
  event,
  index,
  isNow,
  isPast,
  isVisible,
  isLast,
  scaleFactor,
  accentColor,
  textColor,
  textMuted,
  borderColor,
  formatTime,
  uri_transformer
}) => {
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      gap: `${20 * scaleFactor}px`,
      padding: `${20 * scaleFactor}px 0`,
      borderBottom: isLast ? 'none' : `1px solid ${borderColor}`,
      position: 'relative',
      opacity: isVisible ? isPast ? 0.4 : 1 : 0,
      transform: isVisible ? 'translateY(0)' : `translateY(${10 * scaleFactor}px)`,
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [isNow && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'absolute',
        left: `${-32 * scaleFactor}px`,
        top: '50%',
        transform: 'translateY(-50%)',
        width: `${4 * scaleFactor}px`,
        height: `${40 * scaleFactor}px`,
        background: accentColor,
        borderRadius: `${2 * scaleFactor}px`
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: `${70 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${14 * scaleFactor}px`,
          fontWeight: 600,
          color: isNow ? accentColor : textColor
        },
        children: formatTime(event.start_time)
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${12 * scaleFactor}px`,
          color: textMuted,
          marginTop: `${2 * scaleFactor}px`
        },
        children: formatTime(event.end_time)
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: `${4 * scaleFactor}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: `${10 * scaleFactor}px`
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h3", {
          style: {
            margin: 0,
            fontSize: `${16 * scaleFactor}px`,
            fontWeight: 500,
            color: textColor,
            transition: 'color 0.2s',
            ...(isHovered && {
              color: accentColor
            })
          },
          children: event.title
        }), isNow && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            fontSize: `${10 * scaleFactor}px`,
            fontWeight: 600,
            color: accentColor,
            textTransform: 'uppercase',
            letterSpacing: `${1 * scaleFactor}px`
          },
          children: "maintenant"
        })]
      }), event.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        style: {
          margin: 0,
          fontSize: `${13 * scaleFactor}px`,
          color: textMuted,
          lineHeight: 1.5
        },
        children: event.description
      }), event.location && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${12 * scaleFactor}px`,
          color: textMuted,
          fontStyle: 'italic'
        },
        children: event.location
      })]
    }), event.logo_uri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
      src: uri_transformer(event.logo_uri),
      alt: event.title,
      style: {
        width: `${44 * scaleFactor}px`,
        height: `${44 * scaleFactor}px`,
        borderRadius: '50%',
        objectFit: 'cover',
        alignSelf: 'center',
        filter: 'grayscale(100%)',
        transition: 'filter 0.3s ease',
        ...(isHovered && {
          filter: 'grayscale(0%)'
        })
      },
      onError: e => {
        e.target.style.display = 'none';
      }
    })]
  });
};
const MinimalEmptyState = ({
  scaleFactor,
  textMuted,
  accentColor
}) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
  style: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: `${24 * scaleFactor}px`
  },
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      width: `${60 * scaleFactor}px`,
      height: `${1 * scaleFactor}px`,
      background: accentColor
    }
  }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
    style: {
      margin: 0,
      fontSize: `${16 * scaleFactor}px`,
      fontWeight: 300,
      color: textMuted,
      letterSpacing: `${1 * scaleFactor}px`
    },
    children: "Aucun \xE9v\xE9nement"
  }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      width: `${60 * scaleFactor}px`,
      height: `${1 * scaleFactor}px`,
      background: accentColor
    }
  })]
});

// ============================================
// EMPTY STATE DEFAULT
// ============================================

const EmptyState = ({
  scaleFactor,
  textMuted
}) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
  style: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: `${16 * scaleFactor}px`,
    opacity: 0.7
  },
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
    style: {
      fontSize: `${64 * scaleFactor}px`,
      animation: 'float 3s ease-in-out infinite'
    },
    children: "\uD83D\uDCED"
  }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${18 * scaleFactor}px`,
        fontWeight: 600,
        marginBottom: `${8 * scaleFactor}px`
      },
      children: "Aucun \xE9v\xE9nement aujourd'hui"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      style: {
        margin: 0,
        fontSize: `${14 * scaleFactor}px`,
        color: textMuted
      },
      children: "Profitez de cette journ\xE9e tranquille !"
    })]
  })]
});
var _default = exports.default = ScheduleCell;